import { solicitudActividadService } from './solicitudActividadService';
import { supabase, TipoEntidadBalance, ActividadEconomica } from '../lib/supabase';
import { getOwnerFromActividad } from '../types/actividad';
import type {
  Comprobante, ComprobanteFaltante, ComprobanteReciente, DatosReembolso, EntidadDinero,
  HistorialComprobante, Respaldo,
} from '../types/comprobante';
import { blobABase64 } from '../utils/comprobanteImagen';
import { fechaFinMes, fechaInicioMes } from '../utils/dateHelpers';

const DEPOSITO = 'comprobantes';
const SEGUNDOS_ENLACE = 600;


/** Cada fila tiene una sola columna de entidad (movimientos_owner_check, comprobantes_una_entidad). */
export const COLUMNA_ENTIDAD = {
  lumisial: 'lumisial_id',
  diocesis: 'diocesis_id',
  provincia: 'provincia_eclesiastica_id',
} as const;

export type ArchivoParaSubir =
  | { readonly tipo: 'imagen'; readonly paginas: readonly Blob[] }
  | { readonly tipo: 'pdf'; readonly archivo: File };

/** Cuánto vale una respuesta "aún no rige": acota el desfase de una sesión abierta al cruzar la fecha. */
export const VIGENCIA_RESPUESTA_AUN_NO_RIGE_MS = 60_000;

interface RespuestaExigenciaEnCache {
  readonly promesa: Promise<boolean>;
  /** Respuesta ya conocida; indefinida mientras la consulta vuela. */
  resuelta?: boolean;
  /** Infinito mientras la consulta vuela y para `true` (una vez que rige, rige); acotado para `false`. */
  venceEn: number;
}

let exigenciaEnCache: RespuestaExigenciaEnCache | null = null;

export const comprobanteService = {
  /** Sube el archivo por el endpoint (valida contenido y jurisdicción) y devuelve el id del comprobante. */
  async subir(entidad: EntidadDinero, archivo: ArchivoParaSubir): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    const cuerpoArchivo = archivo.tipo === 'imagen'
      ? { tipo: 'imagen', paginas: await Promise.all(archivo.paginas.map((p) => blobABase64(p))) }
      : { tipo: 'pdf', contenido: await blobABase64(archivo.archivo), nombre: archivo.archivo.name };

    const respuesta = await fetch('/api/comprobante', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
      },
      body: JSON.stringify({ entidad: { tipo: entidad.tipo, id: entidad.id }, archivo: cuerpoArchivo }),
    });
    const sinDetalle = `No pudimos subir el comprobante (HTTP ${respuesta.status}).`;
    if (!respuesta.ok) {
      const detalle = await respuesta.json().catch(() => null) as { error?: string } | null;
      throw new Error(detalle?.error ?? sinDetalle);
    }
    const cuerpo = await respuesta.json().catch(() => null) as { data?: { comprobante_id?: string } } | null;
    if (!cuerpo?.data?.comprobante_id) throw new Error(sinDetalle);
    return cuerpo.data.comprobante_id;
  },

  /**
   * ¿Ya rige la exigencia? "Sí" se recuerda; "aún no" vale `VIGENCIA_RESPUESTA_AUN_NO_RIGE_MS`, para
   * que una sesión abierta note el cruce de la fecha. `forzar` salta la caducidad de un "aún no"
   * (para el momento de guardar), incluso si hay una consulta en vuelo. La app no lee ni muestra la fecha de entrada en vigor (H18): la
   * base solo responde sí o no.
   */
  getExigenciaVigente(opciones: { readonly forzar?: boolean } = {}): Promise<boolean> {
    const reutilizable = exigenciaEnCache !== null && Date.now() < exigenciaEnCache.venceEn
      // Forzar nunca reutiliza una respuesta que no sea "ya rige": ni un "aún no" ni una consulta en vuelo
      // (pudo partir antes de la vigencia). Cada entrada solo escribe sobre sí misma: una respuesta vieja
      // que llega tarde no pisa la caché de la consulta nueva.
      && !(opciones.forzar === true && exigenciaEnCache.resuelta !== true);
    if (exigenciaEnCache && reutilizable) return exigenciaEnCache.promesa;
    const consulta = async (): Promise<boolean> => {
      const { data, error } = await supabase.rpc('exigencia_comprobantes_vigente');
      if (error) throw error;
      return data === true;
    };
    const entrada: RespuestaExigenciaEnCache = {
      venceEn: Number.POSITIVE_INFINITY,
      promesa: consulta().then((rige) => {
        entrada.resuelta = rige;
        if (!rige) entrada.venceEn = Date.now() + VIGENCIA_RESPUESTA_AUN_NO_RIGE_MS;
        return rige;
      }, (e: unknown) => {
        if (exigenciaEnCache === entrada) exigenciaEnCache = null;
        throw e;
      }),
    };
    exigenciaEnCache = entrada;
    return entrada.promesa;
  },

  async getById(id: string): Promise<Comprobante | null> {
    const { data, error } = await supabase.from('comprobantes').select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data;
  },

  /** Enlaces temporales (10 min), en el mismo orden que las rutas. */
  async firmarRutas(rutas: readonly string[]): Promise<string[]> {
    const { data, error } = await supabase.storage.from(DEPOSITO).createSignedUrls([...rutas], SEGUNDOS_ENLACE);
    if (error) throw error;
    return data.map((d) => {
      if (d.error || !d.signedUrl) throw new Error('No pudimos abrir el comprobante.');
      return d.signedUrl;
    });
  },

  async getRecientes(entidad: EntidadDinero, limite = 20): Promise<ComprobanteReciente[]> {
    const { data, error } = await supabase
      .from('comprobantes')
      .select('*, movimientos:movimientos!movimientos_comprobante_id_fkey(fecha, monto, descripcion)')
      .eq(COLUMNA_ENTIDAD[entidad.tipo], entidad.id)
      .order('created_at', { ascending: false })
      .limit(limite);
    if (error) throw error;
    // Supabase tipa el embebido como arreglo genérico; el shape real es ComprobanteReciente.
    return (data ?? []) as unknown as ComprobanteReciente[];
  },

  async getFaltantes(entidad: EntidadDinero): Promise<ComprobanteFaltante[]> {
    const { data, error } = await supabase
      .from('comprobantes_faltantes')
      .select('*')
      .eq(COLUMNA_ENTIDAD[entidad.tipo], entidad.id)
      .order('pendiente_desde', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async getFaltantesDelMes(entidad: EntidadDinero, mes: number, anio: number): Promise<ComprobanteFaltante[]> {
    const { data, error } = await supabase
      .from('comprobantes_faltantes')
      .select('*')
      .eq(COLUMNA_ENTIDAD[entidad.tipo], entidad.id)
      .gte('fecha', fechaInicioMes(mes, anio))
      .lte('fecha', fechaFinMes(mes, anio))
      .order('fecha', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  /**
   * Una sola transacción para todos los respaldos. Conserva IDs y argumentos
   * ante respuesta incierta; el contrato comprueba filas, permisos y triggers.
   */
  async adjuntar(
    items: ReadonlyArray<{ readonly movimientoId: string; readonly respaldo: Respaldo }>,
    comprobanteId: string,
  ): Promise<void> {
    const lote = items.map(({ movimientoId, respaldo }) => ({ movimiento_id: movimientoId, respaldo }));
    await solicitudActividadService.ejecutarContrato('adjuntar_comprobantes',
      { p_items: lote, p_comprobante: comprobanteId }, { items: lote, comprobante: comprobanteId });
  },

  /** Marcar un gasto como reembolsado exige el respaldo del pago a la persona (spec, /grill-me). */
  async marcarReembolsado(movimientoId: string, datos: DatosReembolso): Promise<void> {
    await solicitudActividadService.ejecutarContrato('reembolsar_movimiento',
      { p_id: movimientoId, p_datos: datos }, { id: movimientoId, datos });
  },

  /** Advertencia (no bloqueo): el mismo folio de talonario ya usado en la entidad. */
  async hayFolioRepetido(entidad: EntidadDinero, folio: number, excluirMovimientoId?: string): Promise<boolean> {
    let query = supabase
      .from('movimientos')
      .select('id', { count: 'exact', head: true })
      .eq(COLUMNA_ENTIDAD[entidad.tipo], entidad.id)
      .eq('anulado', false)
      .or(`folio_recibo.eq.${folio},reembolso_folio_recibo.eq.${folio}`);
    if (excluirMovimientoId) query = query.neq('id', excluirMovimientoId);
    const { count, error } = await query;
    if (error) throw error;
    return (count ?? 0) > 0;
  },

  async getHistorial(movimientoId: string, respaldo: Respaldo): Promise<HistorialComprobante[]> {
    const { data, error } = await supabase
      .from('movimiento_comprobantes_historial')
      .select('*')
      .eq('movimiento_id', movimientoId)
      .eq('respaldo', respaldo)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  async getCuotaIglesiaPorTraspasar(
    tipoEntidad: TipoEntidadBalance, entidadId: string, mes: number, anio: number,
  ): Promise<number> {
    const { data, error } = await supabase.rpc('cuota_iglesia_por_traspasar', {
      p_tipo_entidad: tipoEntidad, p_entidad_id: entidadId, p_mes: mes, p_anio: anio,
    });
    if (error) throw error;
    return Number(data ?? 0);
  },

  async getEntidadDeActividad(actividadId: string): Promise<EntidadDinero> {
    const { data, error } = await supabase
      .from('actividades_economicas')
      .select('lumisial_id, diocesis_id, provincia_eclesiastica_id')
      .eq('id', actividadId)
      .single();
    if (error) throw error;
    const owner = getOwnerFromActividad(data as ActividadEconomica);
    if (!owner || owner.tipo === 'global') throw new Error('La actividad no tiene una entidad dueña');
    return { tipo: owner.tipo, id: owner.id, nombre: '' };
  },

  async getEntidadDeCampana(campanaId: string): Promise<EntidadDinero> {
    const { data, error } = await supabase.from('campañas').select('lumisial_id').eq('id', campanaId).single();
    if (error) throw error;
    return { tipo: 'lumisial', id: data.lumisial_id as string, nombre: '' };
  },
};
